import React from 'react';
import { Checkbox, TableBody, TableRow } from '@mui/material';
import { Theme } from '@mui/material/styles';
import type { DataRecord } from 'components/DataTable/DataTable.helper';
import DataTableCell from './DataTableCell';

// export interface ItemsType {
//   id: string;
//   [key: string]: any;
// }

export interface DataStructure {
  [key: string]: any;
}

interface DataTableColumnsProps<T extends DataRecord> {
  item: T;
  dataStructure: DataStructure;
  selectable: boolean;
  checkedTableItems: Record<string, boolean>;
  handleChecked: (id: string, selected: boolean) => void;
  actions: React.ReactElement[];
  columnPredicate: (column: string, value: any, item: T) => React.ReactNode;
}

const DataTableColumns = <ItemsType extends DataRecord>({
  item,
  dataStructure,
  selectable,
  checkedTableItems,
  handleChecked,
  actions,
  columnPredicate,
}: DataTableColumnsProps<ItemsType>) => {
  return [
    ...(selectable
      ? [
          <DataTableCell
            key={`cell-column-select-box-${item.id}`}
            keyName={`cell-column-select-box-${item.id}-c`}
            sx={{ padding: '20px 12px 20px 1rem' }}
          >
            <Checkbox
              checked={checkedTableItems[item.id] ?? false}
              onClick={() =>
                handleChecked(item.id, checkedTableItems[item.id] ?? false)
              }
            />
          </DataTableCell>,
        ]
      : []),
    ...Object.keys(dataStructure).map((column, index) => (
      <DataTableCell
        key={`cell-column-${column}-${item[column]?.id}-c`}
        keyName={`cell-column-${column}-${item[column]?.id}`}
        sx={{
          paddingTop: '18px',
          paddingBottom: '18px',
          paddingLeft: !selectable && index > 0 ? '1rem' : '1.5rem',
        }}
      >
        {columnPredicate(column, item[column], item)}
      </DataTableCell>
    )),
    ...(actions.length > 0
      ? [
          <DataTableCell
            key={`cell-column--${item.id}-actions-c`}
            keyName={`cell-column--${item.id}-actions`}
            sx={{ padding: '1.25rem 1rem' }}
          >
            {actions.map((element, index) =>
              React.cloneElement(element, {
                id: item.id,
                key: `data-cell-actions-${item.id}-${index}`,
              }),
            )}
          </DataTableCell>,
        ]
      : []),
  ];
};

interface DataTableBodyProps<ItemsType extends DataRecord> {
  pagedItems: ItemsType[];
  dataStructure: DataStructure;
  selectable: boolean;
  checkedTableItems: Record<string, boolean>;
  handleChecked: (id: string, selected: boolean) => void;
  actions: React.ReactElement[];
  columnPredicate: (
    column: string,
    value: any,
    item: ItemsType,
  ) => React.ReactNode;
}

const DataTableBody = <ItemsType extends DataRecord>({
  pagedItems,
  dataStructure,
  selectable,
  checkedTableItems,
  handleChecked,
  actions,
  columnPredicate,
}: DataTableBodyProps<ItemsType>) => {
  return (
    <TableBody>
      {pagedItems.map((item) => (
        <TableRow
          key={`row-${item.id}-r`}
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.grey[100],
          })}
        >
          <DataTableColumns
            item={item}
            dataStructure={dataStructure}
            selectable={selectable}
            checkedTableItems={checkedTableItems}
            handleChecked={handleChecked}
            actions={actions}
            columnPredicate={columnPredicate}
          />
        </TableRow>
      ))}
    </TableBody>
  );
};

export default DataTableBody;
